/**
 * Engraving Customizer (Bulletproof Version)
 * - Supports multiple product sections on one page
 * - Debug logging enabled
 * - Hidden inputs on the product form + fetch bridge for bundle apps (e.g. DealEasy) that POST /cart/add.js directly
 */

/** Cart attribute: DealEasy __de_bundle_id -> { text, print } (line props often blocked on bundle parents). */
var HARMONY_ENGRAVING_ATTR_KEY = 'harmony_engraving_by_bundle';

/** localStorage: per-page persistence of the engraving textarea so it survives refresh / reopen. */
var HARMONY_ENGRAVING_STORAGE_PREFIX = 'harmonyicons-engraving-text-';
var HARMONY_ENGRAVING_STORAGE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function harmonyEngravingStorageKey() {
  // Scope per URL pathname so each product page keeps its own engraving text.
  // Falls back to a generic key on environments without `location`.
  try {
    return HARMONY_ENGRAVING_STORAGE_PREFIX + (window.location.pathname || 'default');
  } catch (e) {
    return HARMONY_ENGRAVING_STORAGE_PREFIX + 'default';
  }
}

function saveEngravingTextToStorage(text) {
  try {
    if (typeof localStorage === 'undefined') return;
    const value = String(text || '');
    if (!value) {
      localStorage.removeItem(harmonyEngravingStorageKey());
      return;
    }
    localStorage.setItem(
      harmonyEngravingStorageKey(),
      JSON.stringify({ text: value, savedAt: Date.now() })
    );
  } catch (e) {
    /* quota / private mode - ignore */
  }
}

function loadEngravingTextFromStorage() {
  try {
    if (typeof localStorage === 'undefined') return '';
    const raw = localStorage.getItem(harmonyEngravingStorageKey());
    if (!raw) return '';
    const data = JSON.parse(raw);
    if (!data || typeof data.text !== 'string') return '';
    if (
      typeof data.savedAt === 'number' &&
      Date.now() - data.savedAt > HARMONY_ENGRAVING_STORAGE_TTL_MS
    ) {
      localStorage.removeItem(harmonyEngravingStorageKey());
      return '';
    }
    return data.text;
  } catch (e) {
    return '';
  }
}
/** v2 encoding: theme-check-safe in Liquid (no parse_json). */
var HARMONY_ATTR_V2_PREFIX = '[[HARMONY_V2]]';
var HARMONY_ATTR_COL = '[[C]]';
var HARMONY_ATTR_ROW = '[[R]]';

function parseHarmonyBundleAttrRaw(raw) {
  if (!raw || typeof raw !== 'string') return {};
  const s = raw.trim();
  if (s.startsWith(HARMONY_ATTR_V2_PREFIX)) {
    const map = {};
    const pl = s.slice(HARMONY_ATTR_V2_PREFIX.length);
    for (const rec of pl.split(HARMONY_ATTR_ROW)) {
      if (!rec) continue;
      const cols = rec.split(HARMONY_ATTR_COL);
      if (cols.length < 2) continue;
      try {
        map[decodeURIComponent(cols[0])] = {
          text: decodeURIComponent(cols[1] || ''),
          print: cols.length >= 3 ? decodeURIComponent(cols[2] || '') : '',
        };
      } catch (e) {
        /* skip malformed row */
      }
    }
    return map;
  }
  try {
    const j = JSON.parse(s);
    return j && typeof j === 'object' && !Array.isArray(j) ? j : {};
  } catch (e) {
    return {};
  }
}

function stringifyHarmonyBundleMap(map) {
  const parts = [];
  for (const bid of Object.keys(map)) {
    const val = map[bid] || {};
    const t = String(val.text != null ? val.text : '');
    const p = String(val.print != null ? val.print : '');
    parts.push(
      `${encodeURIComponent(bid)}${HARMONY_ATTR_COL}${encodeURIComponent(t)}${HARMONY_ATTR_COL}${encodeURIComponent(p)}`
    );
  }
  return HARMONY_ATTR_V2_PREFIX + parts.join(HARMONY_ATTR_ROW);
}

function getProductFormVariantId(sectionId) {
  const form = document.getElementById(`product-form-${sectionId}`);
  const idInput = form?.querySelector('input[name="id"]');
  if (!idInput?.value) return null;
  const n = Number(idInput.value);
  return Number.isFinite(n) ? n : null;
}

function clampNum(n, min, max, fallback) {
  const x = Number(n);
  if (!Number.isFinite(x)) return fallback;
  return Math.min(max, Math.max(min, x));
}

function defaultEngravingBlockConfig() {
  return {
    defaultBackingUrl: '',
    previewBackingUrl: '',
    previewFontFamily: "Georgia, 'Times New Roman', serif",
    previewFontSizeTenths: 15,
    previewLetterSpacingCentiEm: 10,
    previewLineHeightPercent: 140,
    previewTextColor: '#270503',
    overlayBottomPct: 12,
    overlayLeftPct: 10,
    overlayWidthPct: 80,
    overlayPadPct: 8,
    maxLineWidthPct: 80,
  };
}

function parseEngravingBlockConfig(sectionId) {
  const el = document.getElementById(`harmony-engraving-backing-config-${sectionId}`);
  if (!el) return defaultEngravingBlockConfig();
  try {
    const j = JSON.parse(el.textContent);
    const base = defaultEngravingBlockConfig();
    let u =
      j && j.defaultBackingUrl != null ? String(j.defaultBackingUrl).trim() : '';
    if (!u && j && Array.isArray(j.choices) && j.choices[0] && j.choices[0].url) {
      u = String(j.choices[0].url).trim();
    }
    const pv =
      j && j.previewBackingUrl != null ? String(j.previewBackingUrl).trim() : '';
    const font =
      j && j.previewFontFamily != null && String(j.previewFontFamily).trim()
        ? String(j.previewFontFamily).trim()
        : base.previewFontFamily;
    const sz = Number(j.previewFontSizeTenths);
    const ls = Number(j.previewLetterSpacingCentiEm);
    const lh = Number(j.previewLineHeightPercent);
    const colRaw =
      j && j.previewTextColor != null ? String(j.previewTextColor).trim() : '';
    const col = colRaw || base.previewTextColor;
    return {
      defaultBackingUrl: u,
      previewBackingUrl: pv,
      previewFontFamily: font,
      previewFontSizeTenths:
        Number.isFinite(sz) && sz > 0 ? sz : base.previewFontSizeTenths,
      previewLetterSpacingCentiEm: Number.isFinite(ls) ? ls : base.previewLetterSpacingCentiEm,
      previewLineHeightPercent:
        Number.isFinite(lh) && lh >= 50 ? lh : base.previewLineHeightPercent,
      previewTextColor: col,
      overlayBottomPct: clampNum(j.overlayBottomPct, 0, 48, base.overlayBottomPct),
      overlayLeftPct: clampNum(j.overlayLeftPct, 0, 50, base.overlayLeftPct),
      overlayWidthPct: clampNum(j.overlayWidthPct, 35, 100, base.overlayWidthPct),
      overlayPadPct: clampNum(j.overlayPadPct, 0, 24, base.overlayPadPct),
      maxLineWidthPct: clampNum(j.maxLineWidthPct, 20, 100, base.maxLineWidthPct),
    };
  } catch (e) {
    return defaultEngravingBlockConfig();
  }
}

function parseBackingConfig(sectionId) {
  const c = parseEngravingBlockConfig(sectionId);
  return { defaultBackingUrl: c.defaultBackingUrl, previewBackingUrl: c.previewBackingUrl };
}

/**
 * @param {string} printBgUrl  bg= (download / print asset)
 * @param {string} previewBgUrl  preview_bg= when set and different from print
 * @param {number} [maxLineWidthPct] maxw= when set; max line width as % of canvas
 */
function buildGeneratorUrl(textTrimmed, printBgUrl, previewBgUrl, maxLineWidthPct) {
  const generatorBase = `${window.location.origin}/pages/engraving-generator?view=engraving-generator`;
  if (!textTrimmed || !String(textTrimmed).trim()) {
    return '';
  }
  const text = String(textTrimmed).trim();
  const encodedText = encodeURIComponent(text);
  let out = `${generatorBase}&text=${encodedText}`;
  const p = printBgUrl && String(printBgUrl).trim();
  if (p) out += `&bg=${encodeURIComponent(p)}`;
  const v = previewBgUrl && String(previewBgUrl).trim();
  if (v && v !== p) out += `&preview_bg=${encodeURIComponent(v)}`;
  if (Number.isFinite(maxLineWidthPct) && maxLineWidthPct > 0 && maxLineWidthPct < 100) {
    out += `&maxw=${encodeURIComponent(String(maxLineWidthPct))}`;
  }
  return out;
}

/** Same generator link for all cart lines; optional separate preview backing via theme. */
function buildEngravingGeneratorLinks(sectionId, textTrimmed) {
  if (!textTrimmed || !String(textTrimmed).trim()) {
    return { linkToSend: '', printLinksAll: '' };
  }
  const text = String(textTrimmed).trim();
  const cfg = parseEngravingBlockConfig(sectionId);
  const url = buildGeneratorUrl(
    text,
    cfg.defaultBackingUrl || '',
    cfg.previewBackingUrl || '',
    cfg.maxLineWidthPct
  );
  return { linkToSend: url, printLinksAll: url };
}

function refreshEngravingLineItemProperties(sectionId) {
  const input = document.getElementById(`engraving-text-${sectionId}`);
  const text = input ? input.value : '';
  const trimmed = text.trim();
  const { linkToSend, printLinksAll } =
    trimmed.length > 0
      ? buildEngravingGeneratorLinks(sectionId, trimmed)
      : { linkToSend: '', printLinksAll: '' };
  document.querySelectorAll(`.engraving-link-input-${sectionId}`).forEach((inp) => {
    inp.value = linkToSend;
  });
  document.querySelectorAll(`.engraving-printlinks-input-${sectionId}`).forEach((inp) => {
    inp.value = printLinksAll;
  });
}

function getEngravingBridgePayload() {
  const customizer = document.querySelector('.engraving-customizer[data-section-id]');
  if (!customizer) return null;
  const sectionId = customizer.dataset.sectionId;
  const input = document.getElementById(`engraving-text-${sectionId}`);
  if (!input) return null;

  const text = input.value.trim();
  const textToSend = text.length > 0 ? text : '(No engraving)';
  const { linkToSend, printLinksAll } =
    text.length > 0
      ? buildEngravingGeneratorLinks(sectionId, text)
      : { linkToSend: '', printLinksAll: '' };
  return { sectionId, textToSend, linkToSend, printLinksAll };
}

function mergeEngravingIntoLineItem(item, payload, options) {
  const propName =
    options && options.engravingPropertyName ? options.engravingPropertyName : 'Engraving';
  const props =
    item.properties && typeof item.properties === 'object' && !Array.isArray(item.properties)
      ? { ...item.properties }
      : {};
  delete props.Engraving;
  delete props._Engraving;
  delete props['_Engraving print links'];
  props[propName] = payload.textToSend;
  if (payload.linkToSend) props['_Print File'] = payload.linkToSend;
  if (payload.printLinksAll) props['_Engraving print links'] = payload.printLinksAll;
  return { ...item, properties: props };
}

function enrichCartAddItemsWithEngraving(items, payload) {
  if (!Array.isArray(items) || items.length === 0) return items;

  if (items.length === 1) {
    return [mergeEngravingIntoLineItem(items[0], payload)];
  }

  // One visible `Engraving` (checkout shows line properties per row). Rest use `_Engraving`
  // (hidden at checkout per Shopify; still on the line for admin / apps).
  return items.map((item, i) =>
    mergeEngravingIntoLineItem(item, payload, {
      engravingPropertyName: i === 0 ? 'Engraving' : '_Engraving',
    })
  );
}

function engravingBridgeDebug(...args) {
  if (window.__HARMONY_ENGRAVING_DEBUG) console.log('[Engraving cart bridge]', ...args);
}

/**
 * Parse JSON cart/add body and merge engraving into items[].properties.
 * @returns {string|null} new body or null if not applicable
 */
function tryMergeJsonCartAddBody(bodyString, payload) {
  const trimmed = (bodyString || '').trim();
  if (!trimmed.startsWith('{')) return null;
  try {
    const data = JSON.parse(trimmed);
    if (!data) return null;
    if (Array.isArray(data.items)) {
      const nextItems = enrichCartAddItemsWithEngraving(data.items, payload);
      engravingBridgeDebug('merged JSON items', data.items.length);
      return JSON.stringify({ ...data, items: nextItems });
    }
    // Legacy single-line JSON: { id, quantity, properties? } (no items[] — DealEasy / some themes)
    if (data.id != null || data.variant_id != null) {
      const merged = mergeEngravingIntoLineItem(
        {
          id: data.id ?? data.variant_id,
          quantity: data.quantity != null ? data.quantity : 1,
          properties: data.properties,
        },
        payload
      );
      engravingBridgeDebug('merged JSON single-line add');
      return JSON.stringify({
        ...data,
        id: merged.id,
        quantity: merged.quantity,
        properties: merged.properties,
      });
    }
    return null;
  } catch (e) {
    return null;
  }
}

function mergeFormDataEngraving(body, payload) {
  if (typeof FormData === 'undefined' || !(body instanceof FormData)) return;
  if (!body.has('properties[Engraving]')) {
    body.append('properties[Engraving]', payload.textToSend);
  }
  if (payload.linkToSend && !body.has('properties[_Print File]')) {
    body.append('properties[_Print File]', payload.linkToSend);
  }
  if (payload.printLinksAll && !body.has('properties[_Engraving print links]')) {
    body.append('properties[_Engraving print links]', payload.printLinksAll);
  }
}

/**
 * DealEasy (and similar) POST FormData with items[0][id], items[0][properties][...].
 * Shopify uses per-line properties on each items[n]; root-level properties[Engraving] is ignored for those lines.
 */
function mergeFormDataBundleLineEngraving(formData, payload) {
  const indices = new Set();
  formData.forEach((_, key) => {
    const m = /^items\[(\d+)\]\[id\]$/.exec(key);
    if (m) indices.add(Number(m[1], 10));
  });
  if (indices.size === 0) return;

  const sorted = [...indices].sort((a, b) => a - b);
  const firstIdx = sorted[0];
  sorted.forEach((i) => {
    const name = i === firstIdx ? 'Engraving' : '_Engraving';
    formData.delete(`items[${i}][properties][Engraving]`);
    formData.delete(`items[${i}][properties][_Engraving]`);
    formData.append(`items[${i}][properties][${name}]`, payload.textToSend);

    if (payload.linkToSend) {
      const printKey = `items[${i}][properties][_Print File]`;
      if (!formData.has(printKey)) {
        formData.append(printKey, payload.linkToSend);
      }
    }
    if (payload.printLinksAll) {
      const plKey = `items[${i}][properties][_Engraving print links]`;
      if (!formData.has(plKey)) {
        formData.append(plKey, payload.printLinksAll);
      }
    }
  });

  engravingBridgeDebug('merged FormData bundle lines', [...indices].sort((a, b) => a - b));
}

/**
 * Some apps POST cart/add as application/x-www-form-urlencoded with items[n][id]=…
 */
/**
 * Single-variant urlencoded body: id=…&quantity=… (no items[0][id])
 */
function mergeUrlEncodedSingleLineCartAdd(bodyString, payload) {
  const s = (bodyString || '').trim();
  if (s.startsWith('{') || s.includes('items[')) return null;
  try {
    const params = new URLSearchParams(s);
    if (!params.has('id')) return null;
    params.set('properties[Engraving]', payload.textToSend);
    if (payload.linkToSend) params.set('properties[_Print File]', payload.linkToSend);
    if (payload.printLinksAll) params.set('properties[_Engraving print links]', payload.printLinksAll);
    engravingBridgeDebug('merged urlencoded single-line add');
    return params.toString();
  } catch (e) {
    return null;
  }
}

function mergeUrlEncodedCartAddBody(bodyString, payload) {
  const s = bodyString || '';
  if (s.trim().startsWith('{') || !s.includes('items[') || !s.includes('][id]')) return null;
  try {
    const params = new URLSearchParams(s);
    const indices = new Set();
    for (const key of params.keys()) {
      const m = /^items\[(\d+)\]\[id\]$/.exec(key);
      if (m) indices.add(Number(m[1], 10));
    }
    if (indices.size === 0) return null;
    const sorted = [...indices].sort((a, b) => a - b);
    const firstIdx = sorted[0];
    sorted.forEach((i) => {
      const name = i === firstIdx ? 'Engraving' : '_Engraving';
      params.delete(`items[${i}][properties][Engraving]`);
      params.delete(`items[${i}][properties][_Engraving]`);
      params.set(`items[${i}][properties][${name}]`, payload.textToSend);
      if (payload.linkToSend) {
        params.set(`items[${i}][properties][_Print File]`, payload.linkToSend);
      }
      if (payload.printLinksAll) {
        params.set(`items[${i}][properties][_Engraving print links]`, payload.printLinksAll);
      }
    });
    engravingBridgeDebug('merged urlencoded bundle lines', [...indices].sort((a, b) => a - b));
    return params.toString();
  } catch (e) {
    return null;
  }
}

function cartAddUrlMatches(url) {
  if (url == null || url === '') return false;
  try {
    const path = String(url).startsWith('http')
      ? new URL(url).pathname
      : new URL(String(url), window.location.origin).pathname;
    return /\/cart\/add(\.|\/|$)/.test(path);
  } catch (e) {
    return String(url).indexOf('/cart/add') !== -1;
  }
}

function isCartJsUrl(url) {
  if (url == null || url === '') return false;
  try {
    const u = String(url).startsWith('http')
      ? new URL(url)
      : new URL(String(url), window.location.origin);
    return u.pathname === '/cart.js' || u.pathname.endsWith('/cart.js');
  } catch (e) {
    return /\/cart\.js(\?|#|$)/.test(String(url)) || String(url).indexOf('/cart.js') !== -1;
  }
}

function lineHasEngravingProperty(properties) {
  if (!properties || typeof properties !== 'object') return false;
  for (const k of Object.keys(properties)) {
    const kn = String(k).trim().toLowerCase();
    if ((kn === 'engraving' || kn === '_engraving') && String(properties[k] || '').trim() !== '') {
      return true;
    }
  }
  return false;
}

function extractLineItemsFromCartAddLikeResponse(data) {
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data.items)) return data.items;
  return [];
}

/**
 * Cart transforms / bundle apps sometimes drop custom properties on the first response.
 * Patch lines via Cart API after add. Caller should await before returning fetch response to reduce races.
 */
async function repairMissingEngravingOnCartLines(data, payload, doFetch, options) {
  const variantIdFilter = options && options.variantIdFilter != null ? Number(options.variantIdFilter) : null;
  const items = extractLineItemsFromCartAddLikeResponse(data);
  const toFix = items.filter((it) => {
    if (!it || !it.key || lineHasEngravingProperty(it.properties)) return false;
    if (variantIdFilter != null && Number(it.variant_id) !== variantIdFilter) return false;
    return true;
  });
  if (toFix.length === 0) return;

  const bundleOrder = [];
  const byBundle = new Map();
  for (const it of toFix) {
    const bid =
      it.properties && typeof it.properties['__de_bundle_id'] === 'string'
        ? it.properties['__de_bundle_id']
        : `_line_${it.key}`;
    if (!byBundle.has(bid)) {
      byBundle.set(bid, []);
      bundleOrder.push(bid);
    }
    byBundle.get(bid).push(it);
  }

  engravingBridgeDebug('repair: /cart/change.js for', toFix.length, 'line(s)', bundleOrder.length, 'bundle(s)');

  for (const bid of bundleOrder) {
    const group = byBundle.get(bid);
    for (let idx = 0; idx < group.length; idx++) {
      const it = group[idx];
      const props = {
        ...(it.properties && typeof it.properties === 'object' && !Array.isArray(it.properties)
          ? it.properties
          : {}),
      };
      delete props.Engraving;
      delete props._Engraving;
      delete props['_Engraving print links'];
      const engrName = idx === 0 ? 'Engraving' : '_Engraving';
      props[engrName] = payload.textToSend;
      if (payload.linkToSend) props['_Print File'] = payload.linkToSend;
      if (payload.printLinksAll) props['_Engraving print links'] = payload.printLinksAll;

      try {
        const r = await doFetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: it.key,
            quantity: it.quantity != null ? it.quantity : 1,
            properties: props,
          }),
          credentials: 'same-origin',
        });
        if (!r.ok) engravingBridgeDebug('repair change.js HTTP', r.status);
      } catch (e) {
        engravingBridgeDebug('repair change.js error', e);
      }
    }
  }
}

/**
 * Bundle parent lines (has_components) often reject line property updates; mirror engraving into cart attributes
 * keyed by __de_bundle_id so Liquid can still render it.
 */
async function pushEngravingViaAttributesFromCart(cart, payload, doFetch, variantIdFilter) {
  if (!payload || !cart || !Array.isArray(cart.items)) return;

  const vid = variantIdFilter != null ? Number(variantIdFilter) : null;
  const map = {};
  const raw = cart.attributes && cart.attributes[HARMONY_ENGRAVING_ATTR_KEY];
  if (typeof raw === 'string' && raw.trim()) {
    Object.assign(map, parseHarmonyBundleAttrRaw(raw));
  }

  let changed = false;
  for (const it of cart.items) {
    const bid = it.properties && it.properties['__de_bundle_id'];
    if (!bid || typeof bid !== 'string') continue;
    if (vid != null && Number(it.variant_id) !== vid) continue;
    if (lineHasEngravingProperty(it.properties)) {
      if (map[bid] !== undefined) {
        delete map[bid];
        changed = true;
      }
      continue;
    }
    if (payload.textToSend === '(No engraving)' && map[bid] === undefined) continue;

    const printVal = payload.printLinksAll || payload.linkToSend || '';
    const nextVal = { text: payload.textToSend, print: printVal };
    const prev = map[bid];
    if (prev && prev.text === nextVal.text && prev.print === nextVal.print) continue;

    map[bid] = nextVal;
    changed = true;
  }

  if (!changed) return;

  const nextAttrs = { ...(cart.attributes && typeof cart.attributes === 'object' ? cart.attributes : {}) };
  nextAttrs[HARMONY_ENGRAVING_ATTR_KEY] = stringifyHarmonyBundleMap(map);

  try {
    const r = await doFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: nextAttrs }),
      credentials: 'same-origin',
    });
    if (!r.ok) {
      engravingBridgeDebug('cart/update.js attributes HTTP', r.status, await r.text().catch(() => ''));
    } else {
      engravingBridgeDebug('cart attributes updated for', Object.keys(map).length, 'bundle key(s)');
    }
  } catch (e) {
    engravingBridgeDebug('cart/update.js attributes error', e);
  }
}

async function syncEngravingFullCart(payload, doFetch, variantIdFilter) {
  if (!payload) return;
  try {
    const cr = await doFetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!cr.ok) return;
    const cart = await cr.json();
    await repairMissingEngravingOnCartLines(cart, payload, doFetch, { variantIdFilter });
    await pushEngravingViaAttributesFromCart(cart, payload, doFetch, variantIdFilter);
  } catch (e) {
    engravingBridgeDebug('syncEngravingFullCart error', e);
  }
}

async function maybeRepairAfterCartAddResponse(response, payload, doFetch) {
  if (!payload || !response || !response.ok) return;
  const vid = getProductFormVariantId(payload.sectionId);
  await syncEngravingFullCart(payload, doFetch, vid);
}

function mergeCartAddPayloadIntoBody(body, payload) {
  if (body == null) return null;
  if (typeof body === 'string') {
    const jsonMerged = tryMergeJsonCartAddBody(body, payload);
    if (jsonMerged) return jsonMerged;
    const encMerged = mergeUrlEncodedCartAddBody(body, payload);
    if (encMerged) return encMerged;
    const singleEnc = mergeUrlEncodedSingleLineCartAdd(body, payload);
    if (singleEnc) return singleEnc;
    return null;
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    mergeFormDataEngraving(body, payload);
    mergeFormDataBundleLineEngraving(body, payload);
    return body;
  }
  return null;
}

/**
 * Bundle apps often POST /cart/add or /cart/add.js with JSON and ignore theme form fields.
 * Also handles fetch(new Request(...)) where body lives on the Request, not the second argument.
 */
function installEngravingBundleCartBridge() {
  if (window.__harmonyEngravingCartBridgeInstalled) return;
  window.__harmonyEngravingCartBridgeInstalled = true;

  const origFetch = window.fetch.bind(window);

  function withCartAddRepair(innerPromise, payload) {
    if (!payload) return innerPromise;
    return innerPromise.then(async (response) => {
      await maybeRepairAfterCartAddResponse(response, payload, origFetch);
      return response;
    });
  }

  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__harmonyEngravingUrl = url;
    return xhrOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function (body) {
    const xhr = this;
    const url = this.__harmonyEngravingUrl;
    if (cartAddUrlMatches(url)) {
      const payload = getEngravingBridgePayload();
      if (payload && body != null) {
        const merged = mergeCartAddPayloadIntoBody(body, payload);
        if (merged != null) body = merged;
      }
      if (payload) {
        xhr.addEventListener(
          'load',
          function harmonyEngravingRepair() {
            xhr.removeEventListener('load', harmonyEngravingRepair);
            if (xhr.status < 200 || xhr.status >= 300) return;
            let data;
            try {
              data = JSON.parse(xhr.responseText || 'null');
            } catch (e) {
              return;
            }
            if (data) {
              const vid = getProductFormVariantId(payload.sectionId);
              syncEngravingFullCart(payload, origFetch, vid).catch(() => {});
            }
          },
          false
        );
      }
    }
    return xhrSend.call(this, body);
  };

  function attachCartJsEngravingSync(url, promise) {
    return promise.then(async (response) => {
      if (response && response.ok && isCartJsUrl(url)) {
        const p = getEngravingBridgePayload();
        if (p) {
          clearTimeout(window.__harmonyCartJsSyncT);
          window.__harmonyCartJsSyncT = setTimeout(() => {
            const latest = getEngravingBridgePayload();
            if (!latest) return;
            syncEngravingFullCart(latest, origFetch, getProductFormVariantId(latest.sectionId)).catch(
              () => {}
            );
          }, 200);
        }
      }
      return response;
    });
  }

  window.fetch = function (resource, init) {
    const url =
      typeof resource === 'string'
        ? resource
        : resource && typeof resource === 'object' && 'url' in resource
          ? resource.url
          : '';

    if (!cartAddUrlMatches(url)) {
      return attachCartJsEngravingSync(url, origFetch(resource, init));
    }

    const payload = getEngravingBridgePayload();
    if (!payload) {
      engravingBridgeDebug('no payload (no .engraving-customizer on page)');
      return attachCartJsEngravingSync(url, origFetch(resource, init));
    }

    const bodyFromInit = init && init.body != null ? init.body : null;

    if (bodyFromInit != null) {
      if (typeof bodyFromInit === 'string') {
        const merged = mergeCartAddPayloadIntoBody(bodyFromInit, payload);
        if (merged) {
          return attachCartJsEngravingSync(
            url,
            withCartAddRepair(origFetch(resource, { ...init, body: merged }), payload)
          );
        }
      } else {
        mergeFormDataEngraving(bodyFromInit, payload);
        mergeFormDataBundleLineEngraving(bodyFromInit, payload);
      }
      return attachCartJsEngravingSync(url, withCartAddRepair(origFetch(resource, init), payload));
    }

    if (resource instanceof Request && resource.body != null) {
      const req = resource;
      return attachCartJsEngravingSync(
        url,
        (async () => {
          let text;
          try {
            text = await req.clone().text();
          } catch (e) {
            engravingBridgeDebug('could not read Request body', e);
            return origFetch(resource, init);
          }
          const merged = mergeCartAddPayloadIntoBody(text, payload);
          if (merged) {
            const nextHeaders = new Headers(init?.headers || undefined);
            if (!nextHeaders.has('Content-Type')) {
              const ct = req.headers.get('Content-Type');
              if (ct) nextHeaders.set('Content-Type', ct);
              else {
                const isJson = typeof merged === 'string' && merged.trim().startsWith('{');
                nextHeaders.set('Content-Type', isJson ? 'application/json' : 'application/x-www-form-urlencoded');
              }
            }
            return withCartAddRepair(
              origFetch(req.url, {
                method: init?.method || req.method,
                headers: nextHeaders,
                body: merged,
                credentials: init?.credentials ?? req.credentials,
                cache: init?.cache ?? req.cache,
                redirect: init?.redirect ?? req.redirect,
                referrer: init?.referrer ?? req.referrer,
                referrerPolicy: init?.referrerPolicy ?? req.referrerPolicy,
                mode: init?.mode ?? req.mode,
                keepalive: init?.keepalive,
                signal: init?.signal,
              }),
              payload
            );
          }
          if (typeof FormData !== 'undefined' && text && text.includes('form-data')) {
            /* unlikely as text(); skip */
          }
          engravingBridgeDebug('Request body was not JSON with items[]', (text || '').slice(0, 200));
          return withCartAddRepair(origFetch(resource, init), payload);
        })()
      );
    }

    engravingBridgeDebug('cart/add with no body (check XHR or form submit)');
    return attachCartJsEngravingSync(url, withCartAddRepair(origFetch(resource, init), payload));
  };
}

installEngravingBundleCartBridge();

/** @type {WeakMap<Element, EngravingCustomizer>} */
const engravingInstances = new WeakMap();

let engravingResizeTimer;
function onGlobalEngravingResize() {
  clearTimeout(engravingResizeTimer);
  engravingResizeTimer = setTimeout(() => {
    document.querySelectorAll('.engraving-customizer').forEach((container) => {
      const inst = engravingInstances.get(container);
      if (inst) {
        inst.applyOverlayLayout();
        inst.updateFontSize();
        inst.updateMeasureElement();
      }
    });
  }, 150);
}

function ensureGlobalEngravingResize() {
  if (ensureGlobalEngravingResize.bound) return;
  ensureGlobalEngravingResize.bound = true;
  window.addEventListener('resize', onGlobalEngravingResize);
}

// NOTE: We previously auto-forced a popover preview on "bulk order" pages.
// The preferred UX is the image overlay with temporary switching to the back image,
// so we no longer auto-detect/force a preview mode here.

class EngravingCustomizer {
  constructor(container) {
    if (engravingInstances.has(container)) {
      engravingInstances.get(container).refreshPreviewTypographyFromConfig();
      return;
    }

    console.log('🔧 Initializing Engraving Instance...');
    this.customizer = container;

    // Safety Check: Data Attributes
    if (!this.customizer.dataset.sectionId) {
      console.error('❌ Engraving Error: Missing data-section-id on container');
      return;
    }

    this.sectionId = this.customizer.dataset.sectionId;
    this.previewImageIndex = parseInt(this.customizer.dataset.engravingImageIndex, 10) || 2;
    this.previewMode = (this.customizer.dataset.previewMode || '').trim().toLowerCase(); // 'image' | 'popover' | ''
    
    // Select Elements Scoped to this Section
    this.input = document.getElementById(`engraving-text-${this.sectionId}`);
    this.counter = document.getElementById(`char-count-${this.sectionId}`);
    
    // Config
    this.generatorPageUrl = '/pages/engraving-generator'; 
    this.generatorTemplateName = 'engraving-generator';
    this.assignPreviewBlockConfig(parseEngravingBlockConfig(this.sectionId));
    this.maxLines = 3;
    this.lastValidText = '';

    this.productRoot =
      this.customizer.closest('.product') ||
      this.customizer.closest(`#shopify-section-${this.sectionId}`) ||
      document.getElementById(`shopify-section-${this.sectionId}`);

    // Gallery: Sense/Dawn use #GalleryViewer-{{ section.id }} inside .product__media-wrapper
    this.slider = null;
    if (this.productRoot) {
      this.slider = this.productRoot.querySelector(`#GalleryViewer-${this.sectionId}`);
      if (!this.slider) {
        this.slider = this.productRoot.querySelector('[id^="GalleryViewer-"]');
      }
      if (!this.slider) {
        this.slider = this.productRoot.querySelector('slider-component') || this.productRoot.querySelector('.slider');
      }
    }
    if (!this.slider) {
      this.slider = document.querySelector(`#GalleryViewer-${this.sectionId}`);
    }

    if (this.slider) {
      this.mediaItems = this.slider.querySelectorAll(
        '.product__media-item, .product-media-item, li[data-media-id]'
      );
    } else if (this.productRoot) {
      this.mediaItems = this.productRoot.querySelectorAll(
        '.product__media-item, .product-media-item, li[data-media-id]'
      );
    } else {
      this.mediaItems = document.querySelectorAll('.product__media-item');
    }

    this.previewSlide = this.mediaItems[this.previewImageIndex] || null;
    // On some pages (bulk order apps), there may be fewer images than the normal product page.
    // If the configured index is out of range, fall back to the last available image.
    if (!this.previewSlide && this.mediaItems && this.mediaItems.length > 0) {
      this.previewImageIndex = Math.max(0, this.mediaItems.length - 1);
      this.previewSlide = this.mediaItems[this.previewImageIndex] || null;
    }
    if (!this.previewSlide) {
      console.warn(
        'Engraving: no slide at index',
        this.previewImageIndex,
        '(media count:',
        this.mediaItems.length,
        '). Add a back image as the 3rd media or change data-engraving-image-index.'
      );
    }
    // Default preview mode.
    if (!this.previewMode) this.previewMode = this.previewSlide ? 'image' : 'popover';
    // If the multivariants bulk order app is present, prefer the non-gallery popup preview
    // (app pages often rearrange media/gallery markup and the overlay ends up misplaced).
    if (this.previewMode !== 'image' && this.previewMode !== 'popover') {
      this.previewMode = this.previewSlide ? 'image' : 'popover';
    }
    if (this.previewMode !== 'image' && this.previewMode !== 'popover') this.previewMode = 'popover';
    if (this.customizer.dataset.previewMode && this.previewMode === 'image') {
      // explicitly forced to image: respect it
    }

    // Track what image the user was on before we switch to the back image preview.
    this.restoreImageIndex = null;
    this._engravingHovering = false;

    // Thumbnails: Dawn/Sense use id="Thumbnail-{{ section.id }}-N", not GalleryThumbnail-
    if (this.productRoot) {
      this.thumbnails = this.productRoot.querySelectorAll(`[id^="Thumbnail-${this.sectionId}-"]`);
    } else {
      this.thumbnails = document.querySelectorAll(`[id^="Thumbnail-${this.sectionId}-"]`);
    }
    if (!this.thumbnails || this.thumbnails.length === 0) {
      this.thumbnails = document.querySelectorAll(
        `[id^="GalleryThumbnail-${this.sectionId}"], .thumbnail-list .thumbnail, .thumbnail`
      );
    }

    // If input exists, start the engine
    if (this.input) {
      engravingInstances.set(container, this);
      this.init();
    } else {
      console.error(`❌ Engraving Error: Input #engraving-text-${this.sectionId} not found.`);
    }
  }

  assignPreviewBlockConfig(ecfg) {
    this.previewFontFamily = ecfg.previewFontFamily;
    this.fontSizeRatio = ecfg.previewFontSizeTenths / 1000;
    this.lineHeightRatio = ecfg.previewLineHeightPercent / 100;
    this.letterSpacingEm = ecfg.previewLetterSpacingCentiEm / 100;
    this.previewTextColor = ecfg.previewTextColor;
    this.overlayBottomPct = ecfg.overlayBottomPct;
    this.overlayLeftPct = ecfg.overlayLeftPct;
    this.overlayWidthPct = ecfg.overlayWidthPct;
    this.overlayPadPct = ecfg.overlayPadPct;
    this.maxLineWidthPct = ecfg.maxLineWidthPct;
  }

  getOverlayLayoutMetrics() {
    // Single set of percentages used on every screen. Position is anchored to the
    // image rect (see applyOverlayLayout), so the engraving lands in the same spot
    // on the image regardless of viewport size.
    return {
      bottomPct: this.overlayBottomPct,
      leftPct: this.overlayLeftPct,
      widthPct: this.overlayWidthPct,
      padPct: this.overlayPadPct,
    };
  }

  applyOverlayLayout() {
    if (!this.overlay) return;
    const L = this.getOverlayLayoutMetrics();

    // Try to anchor the overlay to the IMAGE's actual rendered rect so that
    // any padding/margin the slide adds around the image doesn't shift the
    // engraving position between desktop and mobile.
    const img = this.previewSlide ? this.previewSlide.querySelector('img') : null;
    if (img && this.previewSlide) {
      const slideRect = this.previewSlide.getBoundingClientRect();
      const imgRect = img.getBoundingClientRect();
      if (imgRect.width > 0 && imgRect.height > 0 && slideRect.width > 0) {
        const imgLeftInSlide = imgRect.left - slideRect.left;
        const imgTopInSlide = imgRect.top - slideRect.top;
        const imgW = imgRect.width;
        const imgH = imgRect.height;
        const slideH = slideRect.height;

        const overlayLeftPx = imgLeftInSlide + (imgW * L.leftPct) / 100;
        const overlayBottomPx =
          slideH - (imgTopInSlide + imgH) + (imgH * L.bottomPct) / 100;
        const overlayWidthPx = (imgW * L.widthPct) / 100;
        const padPx = (imgW * L.padPct) / 100;

        this.overlay.style.left = `${overlayLeftPx}px`;
        this.overlay.style.right = 'auto';
        this.overlay.style.bottom = `${overlayBottomPx}px`;
        this.overlay.style.top = 'auto';
        this.overlay.style.width = `${overlayWidthPx}px`;
        this.overlay.style.boxSizing = 'border-box';
        this.overlay.style.paddingLeft = `${padPx}px`;
        this.overlay.style.paddingRight = `${padPx}px`;
        this.overlay.style.paddingTop = '0';
        this.overlay.style.paddingBottom = '0';
        return;
      }
    }

    // Fallback: percent of slide (used until the image has measured dimensions).
    this.overlay.style.bottom = `${L.bottomPct}%`;
    this.overlay.style.top = 'auto';
    this.overlay.style.left = `${L.leftPct}%`;
    this.overlay.style.right = 'auto';
    this.overlay.style.width = `${L.widthPct}%`;
    this.overlay.style.boxSizing = 'border-box';
    this.overlay.style.paddingLeft = `${L.padPct}%`;
    this.overlay.style.paddingRight = `${L.padPct}%`;
    this.overlay.style.paddingTop = '0';
    this.overlay.style.paddingBottom = '0';
  }

  /** @returns {number} */
  getPreviewSlideWidthPx() {
    if (!this.previewSlide) return 0;
    const image = this.previewSlide.querySelector('img');
    const el = image || this.previewSlide;
    return el.offsetWidth || el.clientWidth || 0;
  }

  /** @param {number} slideWidth */
  computePreviewFontSizePx(slideWidth) {
    // Font size is a fixed fraction of the rendered image width on every screen,
    // so the engraving text is the same proportional size on desktop and mobile.
    // No minimum is applied (a min would cause the proportion to differ between
    // screens whenever the natural value falls below it). To make text larger,
    // increase the "Preview text size" theme setting.
    return slideWidth * this.fontSizeRatio;
  }

  /** Inner width available for text wrapping (px), from theme layout */
  computeInnerTextMaxWidthPx(slideWidth) {
    const L = this.getOverlayLayoutMetrics();
    const overlayW = slideWidth * (L.widthPct / 100);
    const overlayInner = overlayW * (1 - (2 * L.padPct) / 100);
    // Apply Max line width (% of image width). The actual wrap width is the
    // smaller of the overlay's inner width and the max-line-width setting.
    const maxByLineWidth = slideWidth * ((this.maxLineWidthPct || 100) / 100);
    return Math.max(40, Math.min(overlayInner, maxByLineWidth));
  }

  refreshPreviewTypographyFromConfig() {
    this.assignPreviewBlockConfig(parseEngravingBlockConfig(this.sectionId));
    this.applyOverlayLayout();
    this.updateMeasureElement();
    if (this.input) {
      refreshEngravingLineItemProperties(this.sectionId);
    }
    if (this.overlay && this.overlay.classList.contains('visible') && this.input && this.input.value.length > 0) {
      this.showOverlay(this.input.value);
    } else if (this.textElement) {
      this.updateFontSize();
    }
  }

  attachLiveConfigObserver() {
    if (this._configMo) return;
    if (typeof Shopify === 'undefined' || !Shopify.designMode) return;
    const scriptId = `harmony-engraving-backing-config-${this.sectionId}`;
    let t;
    const schedule = () => {
      clearTimeout(t);
      t = setTimeout(() => this.refreshPreviewTypographyFromConfig(), 80);
    };
    this._configMo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'characterData') {
          const parent = m.target.parentElement;
          if (parent && parent.id === scriptId) {
            schedule();
            return;
          }
        }
        if (m.type === 'childList') {
          for (const n of m.addedNodes) {
            if (n.nodeType === 1 && n.id === scriptId) {
              schedule();
              return;
            }
          }
        }
      }
    });
    this._configMo.observe(this.customizer, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  init() {
    try {
      this.createPreviewSurface();
      if (this.previewMode === 'image') {
        this.applyOverlayLayout();
      }
      // Safety cleanup: if a previous version injected popover/badge elements, remove them
      // so they can't leave behind any stray spacing/styles.
      if (this.previewMode === 'image') {
        const box = this.customizer.querySelector('.engraving-box') || this.customizer;
        const oldPop = box ? box.querySelector('.engraving-preview-popover') : null;
        if (oldPop && oldPop.parentNode) oldPop.parentNode.removeChild(oldPop);
      }
      this.createMeasureElement();
      this.injectHiddenInputs();
      this.attachLiveConfigObserver();
      ensureGlobalEngravingResize();

      // Listeners
      this.input.addEventListener('input', this.handleInput.bind(this));
      this.input.addEventListener('keydown', this.handleKeydown.bind(this));
      this.input.addEventListener('paste', this.handlePaste.bind(this));
      this.input.addEventListener('focus', () => this.onEngravingEnter());
      this.input.addEventListener('blur', () => this.onEngravingExit());
      // Important: only switch previews on explicit focus/click, not hover.

      // Restore saved text from localStorage if the textarea is currently empty.
      // This survives page refresh and persists for 30 days.
      try {
        if (!this.input.value || this.input.value.trim().length === 0) {
          const saved = loadEngravingTextFromStorage();
          if (saved) {
            this.input.value = saved;
            this.lastValidText = saved;
            // Trigger our input flow so counter, hidden inputs, and overlay update.
            this.input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }
      } catch (e) {
        /* ignore */
      }

      refreshEngravingLineItemProperties(this.sectionId);

      // Thumbnail Clicks
      if (this.previewMode === 'image') {
        this.thumbnails.forEach((thumb, index) => {
          thumb.addEventListener('click', () => this.handleImageChange(index));
        });
      }
      
      // Scroll Detection
      if (this.previewMode === 'image' && this.slider) {
        let scrollTimeout;
        this.slider.addEventListener('scroll', () => {
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => this.detectCurrentSlide(), 100);
        });
        this.setupIntersectionObserver();
      }

      this.updateFontSize();
      console.log('✅ Engraving Instance Ready!');
    } catch (err) {
      console.error('❌ Engraving Crash:', err);
    }
  }

  isMobileViewport() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 749px)').matches
    );
  }

  snapshotRestoreIndexIfNeeded() {
    if (this.previewMode !== 'image') return;
    if (this.restoreImageIndex != null) return;
    const cur =
      Number.isFinite(this.currentImageIndex) && this.currentImageIndex != null
        ? this.currentImageIndex
        : null;
    if (cur != null && cur !== this.previewImageIndex) {
      this.restoreImageIndex = cur;
      return;
    }
    this.restoreImageIndex = cur != null ? cur : 0;
  }

  onEngravingEnter(fromHover) {
    const hasText = this.input && this.input.value && this.input.value.trim().length > 0;

    if (this.previewMode === 'popover') {
      if (hasText) this.showPreview(this.input.value);
      return;
    }

    // Mobile: do NOT auto-switch the gallery to the engraving image. The user
    // controls the gallery by swiping. The intersection observer will show the
    // overlay when they swipe to the engraving image.
    if (this.isMobileViewport()) {
      if (
        hasText &&
        this.previewSlide &&
        this.currentImageIndex === this.previewImageIndex
      ) {
        this.showOverlay(this.input.value);
      } else if (!hasText) {
        this.hideOverlay();
      }
      return;
    }

    // Desktop image overlay mode:
    // - When they focus AND there's already text, jump to back image and show overlay.
    if (hasText) {
      this.snapshotRestoreIndexIfNeeded();
      this.switchToImage(this.previewImageIndex);
      this.showOverlay(this.input.value);
    } else if (!fromHover) {
      this.hideOverlay();
    }
  }

  onEngravingExit() {
    if (this.previewMode === 'popover') {
      if (!this._engravingHovering) this.hidePreview();
      return;
    }

    // Mobile: we never switched, so don't restore. Hide overlay only if the user
    // isn't currently viewing the engraving image.
    if (this.isMobileViewport()) {
      if (this.currentImageIndex !== this.previewImageIndex) {
        this.hideOverlay();
      }
      this.restoreImageIndex = null;
      return;
    }

    // Desktop: restore whatever image they were on before we switched.
    this.hideOverlay();
    if (this.restoreImageIndex != null && this.restoreImageIndex !== this.previewImageIndex) {
      this.switchToImage(this.restoreImageIndex);
    }
    this.restoreImageIndex = null;
  }

  injectHiddenInputs() {
    // Prefer the main product form (textarea may use form="product-form-…" for properties[Engraving])
    const form =
      document.getElementById(`product-form-${this.sectionId}`) ||
      this.customizer.closest(`#shopify-section-${this.sectionId}`)?.querySelector('form[action*="/cart/add"]') ||
      this.productRoot?.querySelector('form[action*="/cart/add"]') ||
      document.querySelector(`#shopify-section-${this.sectionId} form[action*="/cart/add"]`);

    if (!form) {
      console.warn('Engraving: no product cart form found for section', this.sectionId);
      return;
    }
    if (!form.querySelector(`.engraving-link-input-${this.sectionId}`)) {
      const linkInput = document.createElement('input');
      linkInput.type = 'hidden';
      linkInput.name = 'properties[_Print File]';
      linkInput.value = '';
      linkInput.classList.add('engraving-link-input');
      linkInput.classList.add(`engraving-link-input-${this.sectionId}`);
      form.appendChild(linkInput);
    }
    if (!form.querySelector(`.engraving-printlinks-input-${this.sectionId}`)) {
      const plInput = document.createElement('input');
      plInput.type = 'hidden';
      plInput.name = 'properties[_Engraving print links]';
      plInput.value = '';
      plInput.classList.add('engraving-printlinks-input');
      plInput.classList.add(`engraving-printlinks-input-${this.sectionId}`);
      form.appendChild(plInput);
    }
  }

  handleInput(e) {
    let text = e.target.value;
    const maxLength = parseInt(this.input.getAttribute('maxlength')) || 77;
    
    refreshEngravingLineItemProperties(this.sectionId);

    // --- VISUALS ---
    const lines = this.countVisualLines(text);
    if (lines > this.maxLines) {
      this.input.value = this.lastValidText;
      text = this.lastValidText;
      this.flashWarning();
    } else {
      this.lastValidText = text;
    }

    // Persist to localStorage (debounced) so the text survives refresh / reopen.
    clearTimeout(this._saveTimer);
    this._saveTimer = setTimeout(() => saveEngravingTextToStorage(text), 250);
    
    if (this.counter) {
      this.counter.textContent = text.length;
      this.counter.parentElement.classList.toggle('near-limit', text.length > maxLength - 10);
    }

    this.customizer.classList.toggle('has-text', text.length > 0);

    const isMobile = this.isMobileViewport();

    // Switch image if needed (desktop only — on mobile the user controls the gallery).
    if (
      !isMobile &&
      this.previewMode === 'image' &&
      text.length > 0 &&
      this.previewSlide &&
      this.currentImageIndex !== this.previewImageIndex
    ) {
      this.snapshotRestoreIndexIfNeeded();
      this.switchToImage(this.previewImageIndex);
    }

    if (text.length > 0) {
      // On mobile, only paint the overlay when the engraving image is actually visible.
      if (
        isMobile &&
        this.previewMode === 'image' &&
        this.currentImageIndex !== this.previewImageIndex
      ) {
        this.hideOverlay();
      } else {
        this.showPreview(text);
      }
    } else {
      this.hidePreview();
    }
  }

  // --- HELPERS ---
  debounce(func, wait) {
    let timeout;
    return (...args) => { clearTimeout(timeout); timeout = setTimeout(() => func.apply(this, args), wait); };
  }

  createMeasureElement() {
    const mid = `engraving-measure-${this.sectionId}`;
    const existing = document.getElementById(mid);
    if (existing) {
      this.measureElement = existing;
      return;
    }
    this.measureElement = document.createElement('div');
    this.measureElement.id = mid;
    this.measureElement.style.cssText =
      'position: absolute; visibility: hidden; left: -9999px; top: 0; white-space: pre-line; word-wrap: break-word; padding: 0.3em 0.6em;';
    this.applyPreviewTypographyToElement(this.measureElement);
    document.body.appendChild(this.measureElement);
  }

  applyPreviewTypographyToElement(el) {
    if (!el) return;
    el.style.fontFamily = this.previewFontFamily;
    el.style.letterSpacing = `${this.letterSpacingEm}em`;
    el.style.color = this.previewTextColor;
  }

  updateMeasureElement() {
    this.measureElement = document.getElementById(`engraving-measure-${this.sectionId}`);
    if (!this.measureElement) return;

    let slideW = this.getPreviewSlideWidthPx();
    if (slideW === 0 && this.input) {
      slideW = this.input.offsetWidth || 280;
    }
    if (slideW === 0) return;

    const fontSize = this.computePreviewFontSizePx(slideW);
    const innerMax = this.computeInnerTextMaxWidthPx(slideW);

    this.applyPreviewTypographyToElement(this.measureElement);
    this.measureElement.style.fontSize = `${fontSize}px`;
    this.measureElement.style.lineHeight = `${this.lineHeightRatio}`;
    this.measureElement.style.maxWidth = `${innerMax}px`;
    this.fontSize = fontSize;
    this.lineHeight = fontSize * this.lineHeightRatio;
  }

  countVisualLines(text) {
    if (!this.measureElement) this.createMeasureElement();
    this.updateMeasureElement();
    if (!this.lineHeight || this.lineHeight <= 0) {
      this.lineHeight = 14 * this.lineHeightRatio;
    }
    this.measureElement.textContent = text || 'X';
    return Math.max(1, Math.round(this.measureElement.offsetHeight / this.lineHeight));
  }

  setupIntersectionObserver() {
    const options = { root: this.slider, threshold: 0.6 };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = Array.from(this.mediaItems).indexOf(entry.target);
          if (index !== -1) {
            this.currentImageIndex = index;
            // Only switch overlay if we manually changed slides
            if (index === this.previewImageIndex && this.input.value.length > 0) {
               this.showOverlay(this.input.value);
            } else {
               this.hideOverlay();
            }
          }
        }
      });
    }, options);
    this.mediaItems.forEach(item => observer.observe(item));
  }

  detectCurrentSlide() {
    // Manual backup for scroll detection
    if (!this.slider || !this.mediaItems.length) return;
    // (Logic identical to previous versions)
  }

  createPreviewSurface() {
    if (this.previewMode === 'popover') {
      this.createPopover();
      return;
    }
    this.createOverlay();
  }

  createOverlay() {
    if (!this.previewSlide) return;
    if (getComputedStyle(this.previewSlide).position === 'static') this.previewSlide.style.position = 'relative';
    
    // Prevent duplicate overlays (per slide)
    const overlayId = `engraving-preview-overlay-${this.sectionId}`;
    if (this.previewSlide.querySelector(`#${overlayId}`)) return;

    // Remove any legacy badge/overlay elements from older versions.
    this.previewSlide
      .querySelectorAll(
        '#engraving-preview-overlay, #engraving-preview-badge, .engraving-preview-badge, [id^="engraving-preview-badge-"]'
      )
      .forEach((n) => n && n.parentNode && n.parentNode.removeChild(n));

    this.overlay = document.createElement('div');
    this.overlay.id = overlayId;
    this.overlay.className = 'engraving-preview-overlay';
    this.overlay.innerHTML = `<span id="engraving-preview-text-${this.sectionId}"></span>`;
    this.previewSlide.appendChild(this.overlay);
    this.textElement = this.overlay.querySelector(`#engraving-preview-text-${this.sectionId}`);

    // Badge removed per UX request (keep the overlay text only).
    this.applyOverlayLayout();
  }

  createPopover() {
    // Anchor to the engraving box so it won't affect other layout.
    const box = this.customizer.querySelector('.engraving-box') || this.customizer;
    if (!box) return;

    if (getComputedStyle(box).position === 'static') box.style.position = 'relative';

    const popId = `engraving-preview-popover-${this.sectionId}`;
    const existing = box.querySelector(`#${popId}`);
    if (existing) {
      this.popover = existing;
      this.textElement = existing.querySelector('.engraving-preview-popover__text');
    } else {
      const pop = document.createElement('div');
      pop.id = popId;
      pop.className = 'engraving-preview-popover';
      pop.innerHTML = `
        <div class="engraving-preview-popover__label">Engraving preview</div>
        <div class="engraving-preview-popover__text"></div>
      `;
      box.appendChild(pop);
      this.popover = pop;
      this.textElement = pop.querySelector('.engraving-preview-popover__text');
    }

    this.applyPreviewTypographyToElement(this.textElement);

    // Hover behavior is handled in init() so it can apply consistently.
  }

  updateFontSize() {
    if (!this.textElement) return;
    // Popover mode: base sizing on the input width (available on bulk order pages).
    if (this.previewMode === 'popover') {
      const w = (this.input && (this.input.offsetWidth || this.input.clientWidth)) || 280;
      const fontSize = Math.max(12, Math.min(28, w * this.fontSizeRatio * 1.2));
      this.applyPreviewTypographyToElement(this.textElement);
      this.textElement.style.fontSize = `${fontSize}px`;
      this.textElement.style.lineHeight = `${this.lineHeightRatio}`;
      this.textElement.style.maxWidth = `${Math.max(220, w)}px`;
      return;
    }

    if (!this.previewSlide || !this.overlay) return;
    const width = this.getPreviewSlideWidthPx();
    if (width === 0) return;

    this.applyOverlayLayout();
    this.applyPreviewTypographyToElement(this.textElement);
    const fontSize = this.computePreviewFontSizePx(width);
    const innerMax = this.computeInnerTextMaxWidthPx(width);
    this.textElement.style.fontSize = `${fontSize}px`;
    this.textElement.style.lineHeight = `${this.lineHeightRatio}`;
    this.textElement.style.maxWidth = `${innerMax}px`;
    // Don't force a 3-line tall overlay; with align-items: flex-end, letting the
    // overlay size to the actual text means the bottom of the visible text always
    // matches the configured "distance from bottom" exactly.
    this.overlay.style.height = 'auto';
  }

  handleKeydown(e) {
    if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab'].includes(e.key) || e.ctrlKey || e.metaKey) return;
    const cursorPos = this.input.selectionStart;
    const newChar = e.key === 'Enter' ? '\n' : (e.key.length === 1 ? e.key : '');
    if (newChar && this.countVisualLines(this.input.value.slice(0, cursorPos) + newChar + this.input.value.slice(this.input.selectionEnd)) > this.maxLines) { e.preventDefault(); this.flashWarning(); }
  }

  handlePaste(e) {
    e.preventDefault();
    // (Paste logic same as previous)
    const pastedText = (e.clipboardData || window.clipboardData).getData('text');
    const cursorPos = this.input.selectionStart;
    let trimmedPaste = pastedText;
    while (trimmedPaste.length > 0 && this.countVisualLines(this.input.value.slice(0, cursorPos) + trimmedPaste + this.input.value.slice(this.input.selectionEnd)) > this.maxLines) { trimmedPaste = trimmedPaste.slice(0, -1); }
    if (trimmedPaste.length > 0) {
      const finalText = this.input.value.slice(0, cursorPos) + trimmedPaste + this.input.value.slice(this.input.selectionEnd);
      this.input.value = finalText;
      this.input.selectionStart = this.input.selectionEnd = cursorPos + trimmedPaste.length;
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (trimmedPaste.length < pastedText.length) this.flashWarning();
  }

  flashWarning() {
    this.input.style.transition = 'background-color 0.1s';
    this.input.style.backgroundColor = '#fee2e2';
    setTimeout(() => { this.input.style.backgroundColor = ''; }, 200);
  }

  switchToImage(index) {
    const root = this.productRoot || document;

    // 0) Dawn/Sense style thumbnails are often buttons with aria-controls pointing at the main viewer.
    // Bulk order pages may keep these but change ids, so prefer this path.
    const thumbButtons = root.querySelectorAll(
      'button.thumbnail[aria-controls], [role="button"].thumbnail[aria-controls], a.thumbnail[aria-controls]'
    );
    if (thumbButtons && thumbButtons.length > 0) {
      const btn = thumbButtons[index] || null;
      if (btn && typeof btn.click === 'function') {
        btn.click();
        return;
      }
      // If index is out of range, click the last (common when only 2 thumbs exist).
      const last = thumbButtons[thumbButtons.length - 1];
      if (last && typeof last.click === 'function') {
        last.click();
        return;
      }
    }

    // 0) If we can identify the media id for the target slide, prefer clicking a control that
    // actually drives the main gallery (bulk apps sometimes clone thumbnails).
    const targetEl = this.mediaItems && this.mediaItems[index] ? this.mediaItems[index] : null;
    const mediaId =
      (targetEl && targetEl.getAttribute && targetEl.getAttribute('data-media-id')) ||
      (targetEl && targetEl.dataset && (targetEl.dataset.mediaId || targetEl.dataset.media));

    if (mediaId) {
      const byTarget =
        root.querySelector(`[data-target="${mediaId}"]`) ||
        root.querySelector(`[data-media-id="${mediaId}"]`) ||
        root.querySelector(`[data-thumbnail-media-id="${mediaId}"]`) ||
        root.querySelector(`[aria-controls="${mediaId}"]`);
      if (byTarget && typeof byTarget.click === 'function') {
        byTarget.click();
        return;
      }
    }

    // 1) Try the known Dawn/Sense thumbnail ids first.
    if (this.thumbnails && this.thumbnails[index]) {
      this.thumbnails[index].click();
      return;
    }

    // 2) Bulk order apps may render their own thumbnail buttons without the normal ids.
    // Try data-position based selectors (1-indexed positions are common).
    const pos = index + 1;
    const altThumb =
      root.querySelector(`[data-media-position="${pos}"]`) ||
      root.querySelector(`[data-thumbnail-position="${pos}"]`) ||
      root.querySelector(`[data-position="${pos}"][id*="Thumbnail" i]`) ||
      root.querySelector(`[aria-controls*="Slide-${pos}" i]`) ||
      root.querySelector(`[aria-controls*="-main-${pos}" i]`) ||
      root.querySelector(`[aria-label*="Slide ${pos}" i]`);
    if (altThumb && typeof altThumb.click === 'function') {
      altThumb.click();
      return;
    }

    // 3) Last resort: scroll the target media item into view.
    if (this.slider && this.mediaItems && this.mediaItems[index]) {
      this.mediaItems[index].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }

  showPreview(text) {
    if (this.previewMode === 'popover') {
      if (!this.popover) this.createPopover();
      if (!this.popover) return;
      if (this.textElement) this.textElement.textContent = text;
      this.popover.classList.add('is-visible');
      this.updateFontSize();
      return;
    }
    this.showOverlay(text);
  }

  hidePreview() {
    if (this.previewMode === 'popover') {
      if (this.popover) this.popover.classList.remove('is-visible');
      return;
    }
    this.hideOverlay();
  }

  showOverlay(text) {
    if (!this.overlay) this.createOverlay(); // Ensure overlay exists
    if (!this.overlay) return;
    if (this.textElement) this.textElement.textContent = text;
    this.overlay.classList.add('visible');
    if (this.previewSlide) this.previewSlide.classList.add('engraving-slide-fit');
    this.updateFontSize();
  }

  hideOverlay() {
    if (this.overlay) this.overlay.classList.remove('visible');
    if (this.previewSlide) this.previewSlide.classList.remove('engraving-slide-fit');
  }

  handleImageChange(index) {
     // Triggered by click or scroll
     this.currentImageIndex = index;
     if (this.previewMode === 'image' && index === this.previewImageIndex && this.input.value.length > 0) {
        this.showOverlay(this.input.value);
     } else {
        this.hideOverlay();
     }
  }
}

// --- INITIALIZATION (Updated) ---
function initEngraving(event) {
  let root = document;
  if (event && event.type === 'shopify:section:load' && event.target && event.target.nodeType === 1) {
    root = event.target;
  }
  const customizers = root.querySelectorAll('.engraving-customizer');
  if (customizers.length === 0) return;

  console.log(`🔧 Found ${customizers.length} Engraving Instances. Starting...`);
  customizers.forEach((container) => new EngravingCustomizer(container));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initEngraving());
} else {
  initEngraving();
}

document.addEventListener('shopify:section:load', initEngraving);